import React from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import * as Yup from 'yup'

import { Field, FieldArray } from 'formik'
import { IconTrash } from '@tabler/icons-react'
import { Button } from '@/components/buttons/Button'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { Card } from '@/components/cards/Card'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { closeModal } from '@/reducers/ui/ui.redux'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { TextField, Checkbox } from '@/components/forms/formik'
import { arrayToStr } from '../helpers'

// Concise, Keycloak-realm-safe slug of the org name to prefill the realm field.
// Matches the auth-service realm pattern (^[a-z0-9][a-z0-9_-]*$, <= 50 chars).
const slugifyRealm = (name) =>
  (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
    .replace(/-+$/g, '')

export const AuthorizationOptions = ({
  desktopAuthKey,
  desktopAuthIPs,
  organizationUuid,
  organizationName,
  cloudSSOConfig,
  desktopSSOConfig,
  deleteDesktopTokens,
  handleUpdateDesktopAuthIPs,
  handleUpdateDesktopAuthKey,
  handleUpdateCloudSSO,
  handleUpdateDesktopSSO,
  handleProvisionDesktopSSO,
}) => {
  const dispatch = useDispatch()
  const hasDesktopSSOConfigs = !isEmpty(desktopSSOConfig?.desktop_sso_entries)
  const formattedAuthIPs = arrayToStr(desktopAuthIPs)

  return (
    <>
      <h3>Authorization</h3>

      <div className="organization-buttons-bar">
        <Card>
          <h3>Manage Desktop Key</h3>

          <ButtonAndFormModal
            buttonLabel="Manage Key"
            modalTitle="Manage Desktop Key"
            modalId="organizations/manageDesktopKey"
            modalProps={{ size: 'tiny' }}
            form={
              <AbstractForm
                schema={[
                  {
                    name: 'desktop_auth_key',
                    type: 'text',
                    label: 'Desktop Key',
                    placeholder: 'Enter desktop key',
                    customValidators: [
                      Yup.string()
                        .required('Desktop Key is required')
                        .min(9, 'Must be at least nine characters long'),
                    ],
                  },
                  {
                    name: 'confirm_checkbox',
                    type: 'checkbox',
                    label: 'I want to make changes',
                    required: true,
                  },
                ]}
                existingValues={{ desktop_auth_key: desktopAuthKey || '' }}
                onSubmit={handleUpdateDesktopAuthKey}
                buttonLabel="Save"
                onClose={() => dispatch(closeModal())}
                isModal
              />
            }
          />
        </Card>
        <Card>
          <h3>Manage Authorized IPs</h3>
          <ButtonAndFormModal
            buttonLabel="Manage IPs"
            modalTitle="Authorized IPs"
            modalContent={
              <p>
                Enter IP addresses in the format <code>0.0.0.0</code>, separated by{' '}
                <strong>commas</strong>.
              </p>
            }
            modalId="organizations/manageIPs"
            modalProps={{ size: 'tiny' }}
            form={
              <AbstractForm
                schema={[
                  {
                    name: 'desktop_auth_ips',
                    type: 'textArea',
                    label: 'Authorized IPs',
                    placeholder: 'Enter IP addresses in the format 0.0.0.0, separated by commas',
                  },
                  {
                    name: 'confirm_checkbox',
                    type: 'checkbox',
                    label: 'I want to make changes',
                    required: true,
                  },
                ]}
                existingValues={{ desktop_auth_ips: formattedAuthIPs }}
                onSubmit={handleUpdateDesktopAuthIPs}
                buttonLabel="Save"
                onClose={() => dispatch(closeModal())}
                isModal
              />
            }
          />
        </Card>

        <Card>
          <h3>Delete All Desktop Tokens</h3>
          <BasicModal
            trigger={(handleOpen) => (
              <Button onClick={handleOpen} data-testid="delete-tokens-default">
                Delete Tokens
              </Button>
            )}
            title="Delete All Tokens"
            size="tiny"
          >
            {(handleClose) => (
              <ModalActions onClose={handleClose} onSubmit={deleteDesktopTokens} isDelete>
                <p>
                  Are you sure you want to delete all user desktop tokens? Every user will be
                  required to authenticate with a desktop key or IP address.
                </p>
              </ModalActions>
            )}
          </BasicModal>
        </Card>

        <Card>
          <h3>Manage Desktop SSO (OIDC)</h3>
          <ButtonAndFormModal
            buttonLabel="Manage Desktop SSO"
            modalTitle="Manage Desktop SSO (OIDC)"
            modalId="organizations/manageDesktopSSO"
            modalProps={{ size: 'small' }}
            form={
              <AbstractForm
                schema={[
                  {
                    name: 'desktop_sso_entries',
                    raw: true,
                    as: (
                      <FieldArray name="desktop_sso_entries">
                        {({ push, remove, form }) => {
                          const entries = Array.isArray(form.values.desktop_sso_entries)
                            ? form.values.desktop_sso_entries
                            : []
                          return (
                            <div>
                              <label>
                                <strong>Desktop SSO Configs</strong>
                              </label>
                              {entries.map((entry, index) => (
                                <div
                                  key={entry.id || index}
                                  style={{
                                    border: '1px solid var(--color-border)',
                                    padding: '10px',
                                    marginBottom: '12px',
                                  }}
                                >
                                  <Field
                                    name={`desktop_sso_entries[${index}].domain`}
                                    placeholder="e.g. example.com"
                                    label="Domain"
                                    component={TextField}
                                    required
                                  />
                                  <Field
                                    name={`desktop_sso_entries[${index}].realm`}
                                    placeholder="e.g. my-realm"
                                    label="Realm"
                                    component={TextField}
                                    required
                                  />
                                  <Field
                                    name={`desktop_sso_entries[${index}].alias`}
                                    placeholder="e.g. my-alias"
                                    label="Alias"
                                    component={TextField}
                                    required
                                  />
                                  <Field
                                    name={`desktop_sso_entries[${index}].prevent_non_sso_auth`}
                                    label="Prevent Non SSO Auth"
                                    type="checkbox"
                                    component={Checkbox}
                                  />
                                  <Field
                                    name={`desktop_sso_entries[${index}].auto_provision`}
                                    label="Auto Provision"
                                    type="checkbox"
                                    component={Checkbox}
                                  />
                                  <Button
                                    type="button"
                                    square
                                    icon={<IconTrash />}
                                    onClick={() => remove(index)}
                                    style={{ marginTop: '8px' }}
                                  />
                                </div>
                              ))}
                              <div style={{ marginTop: '1rem' }}>
                                <Button
                                  primary
                                  type="button"
                                  onClick={() => {
                                    // default all fields apart from domain to be the same as first entry
                                    // since usually only domain is changing
                                    const firstEntry = entries[0]
                                    push({
                                      domain: '',
                                      realm: firstEntry?.realm || '',
                                      alias: firstEntry?.alias || '',
                                      prevent_non_sso_auth:
                                        firstEntry?.prevent_non_sso_auth ?? false,
                                      auto_provision: firstEntry?.auto_provision ?? true,
                                    })
                                  }}
                                >
                                  + Add Desktop SSO Config
                                </Button>
                              </div>
                            </div>
                          )
                        }}
                      </FieldArray>
                    ),
                  },
                ]}
                existingValues={desktopSSOConfig}
                onSubmit={handleUpdateDesktopSSO}
                buttonLabel="Save"
                onClose={() => dispatch(closeModal())}
                isModal
                customValidationSchema={Yup.object().shape({
                  desktop_sso_entries: Yup.array().of(
                    Yup.object().shape({
                      domain: Yup.string().required('Domain is required'),
                      realm: Yup.string().required('Realm is required'),
                      alias: Yup.string().required('Alias is required'),
                    })
                  ),
                })}
              />
            }
          />
          {!hasDesktopSSOConfigs && (
            <ButtonAndFormModal
              buttonLabel="Setup from Scratch"
              buttonProps={{ style: { marginLeft: '0.5rem' } }}
              modalTitle="Setup Desktop SSO (OIDC)"
              modalId="organizations/provisionDesktopSSO"
              modalProps={{ size: 'small' }}
              modalContent={
                <p>
                  Creates the Keycloak realm, client, and identity provider, wires the realm to use
                  it, and creates a config for each domain. The response includes the redirect URI
                  to register on the customer&apos;s IdP.
                </p>
              }
              form={
                <AbstractForm
                  schema={[
                    {
                      name: 'realm',
                      type: 'text',
                      label: 'Realm',
                      placeholder: 'e.g. digital-trust (lowercase, no spaces)',
                      defaultValue: slugifyRealm(organizationName),
                      autoComplete: 'off',
                      required: true,
                    },
                    {
                      name: 'provider_type',
                      type: 'text',
                      label: 'Provider',
                      placeholder: 'e.g. microsoft, google, entra, okta',
                      autoComplete: 'off',
                      required: true,
                    },
                    {
                      name: 'idp_alias',
                      type: 'text',
                      label: 'IdP Alias (optional)',
                      placeholder: 'defaults to {realm}-{provider}',
                    },
                    {
                      name: 'well_known_url',
                      type: 'text',
                      label: 'OIDC Discovery URL',
                      placeholder:
                        'https://login.microsoftonline.com/<tenant>/.well-known/openid-configuration',
                      required: true,
                    },
                    {
                      name: 'client_id',
                      type: 'text',
                      label: 'Client ID',
                      required: true,
                    },
                    {
                      name: 'client_secret',
                      type: 'password',
                      inputType: 'password',
                      label: 'Client Secret',
                      autoComplete: 'new-password',
                      required: true,
                    },
                    {
                      name: 'domains',
                      type: 'textArea',
                      label: 'Domains',
                      placeholder: 'one per line or comma-separated, e.g. example.com',
                      required: true,
                    },
                    {
                      name: 'prevent_non_sso_auth',
                      type: 'checkbox',
                      label: 'Prevent Non SSO Auth',
                      toggle: true,
                      defaultValue: true,
                    },
                    {
                      name: 'auto_provision',
                      type: 'checkbox',
                      label: 'Auto Provision',
                      toggle: true,
                      defaultValue: true,
                    },
                  ]}
                  onSubmit={(values) => {
                    const { domains, idp_alias, ...rest } = values
                    const parsedDomains = String(domains || '')
                      .split(/[\s,]+/)
                      .map((domain) => domain.trim().toLowerCase())
                      .filter(Boolean)
                    // Return the thunk so Formik awaits it and resets isSubmitting;
                    // without this the submit button spins forever after an error.
                    return handleProvisionDesktopSSO({
                      ...rest,
                      domains: parsedDomains,
                      ...(idp_alias ? { idp_alias } : {}),
                    })
                  }}
                  buttonLabel="Provision"
                  onClose={() => dispatch(closeModal())}
                  isModal
                />
              }
            />
          )}
        </Card>

        <Card>
          <h3>Manage Cloud SSO (SAML)</h3>
          <ButtonAndFormModal
            buttonLabel="Manage Cloud SSO"
            modalTitle="Manage Cloud SSO (SAML)"
            modalContent={
              <>
                <p>
                  NOTE: legacy integrations may have entity ID `baltosoftware` and ACS URI with path
                  `saml/sso` (instead of `saml/sso/acs`). That should work, but if they are having
                  issues please have them update to use the new values.
                </p>
                <p>
                  <strong>Audience/EntityID</strong>
                  <br />
                  {`https://baltocloud.com/sp/${organizationUuid}`}
                </p>
                <p>
                  <strong>Recipient/ACS URI</strong>
                  <br />
                  {`${window.location.origin}/saml/sso/acs/${organizationUuid}`}
                </p>
              </>
            }
            modalId="organizations/manageCloudSSO"
            modalProps={{ size: 'small' }}
            form={
              <AbstractForm
                schema={[
                  {
                    name: 'metadata_url',
                    type: 'text',
                    label: 'SAML Metadata URL',
                    placeholder: 'https://sso-provider.com/metadata.xml',
                    required: true,
                  },
                  {
                    name: 'cloud_sso_entries',
                    raw: true,
                    as: (
                      <FieldArray name="cloud_sso_entries">
                        {({ push, remove, form }) => {
                          const entries = Array.isArray(form.values.cloud_sso_entries)
                            ? form.values.cloud_sso_entries
                            : []
                          return (
                            <div>
                              <label>
                                <strong>Cloud SSO Configs</strong>
                              </label>
                              {entries.map((entry, index) => (
                                <div
                                  key={entry.id || index}
                                  style={{
                                    border: '1px solid var(--color-border)',
                                    padding: '10px',
                                    marginBottom: '12px',
                                  }}
                                >
                                  <Field
                                    name={`cloud_sso_entries[${index}].domain`}
                                    placeholder="e.g. example.com"
                                    label="Domain"
                                    component={TextField}
                                  />
                                  <Field
                                    name={`cloud_sso_entries[${index}].prevent_non_sso_auth`}
                                    label="Prevent Non SSO Auth"
                                    type="checkbox"
                                    component={Checkbox}
                                  />
                                  <Field
                                    name={`cloud_sso_entries[${index}].auto_provision`}
                                    label="Auto Provision"
                                    type="checkbox"
                                    component={Checkbox}
                                  />
                                  <Button
                                    aria-label={`Remove cloud SSO config ${index + 1}`}
                                    type="button"
                                    square
                                    icon={<IconTrash />}
                                    onClick={() => remove(index)}
                                    style={{ marginTop: '8px' }}
                                  />
                                </div>
                              ))}
                              <div style={{ marginTop: '1rem' }}>
                                <Button
                                  primary
                                  type="button"
                                  onClick={() =>
                                    push({
                                      metadata_url: '',
                                      prevent_non_sso_auth: false,
                                      auto_provision: true,
                                      domain: '',
                                    })
                                  }
                                >
                                  + Add Cloud SSO Config
                                </Button>
                              </div>
                            </div>
                          )
                        }}
                      </FieldArray>
                    ),
                  },
                ]}
                existingValues={cloudSSOConfig}
                onSubmit={handleUpdateCloudSSO}
                buttonLabel="Save"
                onClose={() => dispatch(closeModal())}
                isModal
              />
            }
          />
        </Card>
      </div>
    </>
  )
}
